/**
 * Which pages of one site compete with each other for the same searches (quality sprint 7, workstream
 * A). The pure rules: what counts as one search, when two pages really share it, how strong the
 * overlap is and how it splits. The data is gathered read-only by
 * `backend/src/services/competing-pages.ts`; the verdict a pair gets (merge, differentiate, keep both)
 * is not decided here, only the evidence it rests on.
 *
 * Two evidence sources, never mixed for one domain (owner, 2026-10-20):
 *
 * - **Search Console** (a connection covering the domain with synced data): two pages share a search
 *   when both get impressions for it in the window. Search Console reports every page Google showed
 *   for a query, so the query is matched word for word.
 * - **Estimated** (no Search Console): stored ranked keywords, stored result sets and the search each
 *   page is compared on. The ranked-keywords dump gives each keyword to ONE page (on the sprint 7
 *   frozen data, 0 of 5,266 SlideSpeak keywords and 1 of 1,550 ManyRequests keywords sit on two
 *   pages), so two pages meet only on the same search worded differently. `competingSearchKey` is
 *   that test, and it is deliberately narrow: an equivalence of searches, not a word-overlap score.
 *   General word overlap cannot judge relevance (TECHNICAL_DEBT §0ag); this only recognises one
 *   search typed another way.
 *
 * Round 2 (after an independent grade of round 1's calls): the overlap must matter to both pages
 * (`COMPETING_PAIR_MIN_BOTH_SHARE`); a page's own search counts only when the other page holds it on
 * page one (`ownSearchHeld`); two copies of one page (same title or H1, both self-canonical) compete at
 * any position (`duplicate`); a search for the reverse conversion is not the page's
 * (`searchRunsAgainstPageDirection`). The loader also sets aside pages the latest crawl found
 * redirected or gone. Counts in the constants' comments are round 1's unless they say otherwise;
 * round 2 finds every labelled pair round 1 did and no extra on either queue.
 *
 * Round 3 (after a second independent grade, on a new sample): (a) the page that holds another's own
 * search must hold it with search demand behind it (`rankedPosition`: a Search Console row or a
 * ranked keyword with volume); a stored result set alone, on a query nobody is recorded searching,
 * holds nothing. (b) A tool and an article on the same task are different jobs (`differentJobs`,
 * read from the product's own page-type vote, `pageTypeFamilyWithoutSerp`, not from the address).
 * (c) A shared search a THIRD page of the site holds clearly better (more impressions or volume than
 * the two together AND a better position than either) is that page's (`heldBy`) and makes neither
 * pair compete. On the frozen data: ManyRequests 56 → 54 competing pairs, SlideSpeak 25 → 13; the
 * labelled set still 6 of 6 and 2 of 2 outside the homepage, no extra on either queue. Tried and
 * dropped: a "top search of both pages" path (no effect on any graded or labelled pair up to the
 * top 4, adds a graded-wrong pair at 5, and the missed pair it was for ranks 11th on one side); a
 * third page holding the search on weight alone, at any position (loses a labelled pair at 1x the
 * pair's weight, and only helps in a 1.5x to 2x window: a tune, not a rule).
 *
 * A pair is listed when the two pages share at least one search. It **competes** only when a shared
 * search is contested (both pages really show for it, neither is a passing appearance) and the
 * contested searches are a real part of one page's demand. A listed pair that does not compete is
 * "keep both" evidence: the verdict layer must not turn it into advice.
 */

export type CompetingEvidenceSource = 'search_console' | 'estimated';

/** Where one observation of a page on a search came from. */
export type CompetingObservationKind =
  /** Search Console: the page had impressions for the query in the window. */
  | 'search_console'
  /** The page's stored ranked keyword (DataForSEO estimate). */
  | 'ranked_keyword'
  /** The page appears in a stored result set fetched for another page's comparison search. */
  | 'result_set'
  /** The search this page is compared on (its stored result set, not held back). */
  | 'comparison_search';

/**
 * Deepest position at which a page still really shows for a search; deeper is a passing appearance.
 * The labelled set's grader drew the line at "both better than about #40; #50 or worse is passing".
 *
 * Measured on the sprint 7 frozen data against the labelled set (both sites):
 * - ManyRequests (Search Console averages): all six labelled pairs are found from 35 to 50; at 30 one
 *   is lost (the larger page sits at #30 to #37 on most of the searches it shares). Labelled "no"
 *   pairs found: 2 at 35 to 45, 5 at 50.
 * - SlideSpeak (stored positions): both labelled pairs away from the homepage are found from 20 to 50;
 *   at 50 the homepage's reworded generator searches (#47 and deeper) start to count.
 * Margin: 5 places below, 10 above.
 */
export const COMPETING_SEARCH_MAX_POSITION = 40;

/**
 * Search Console only: impressions each page needs on a search before it counts as shown for it. A
 * page with a few impressions on another page's head search was tried by Google, not shown.
 * ManyRequests: all six labelled pairs hold from 1 to 18 (the thinnest contested side has 18); at 20
 * two are lost. Not-listed extras on queue pages: 10 at 1, 3 at 10, 1 at 15. 10 is a round number
 * inside the plateau, not fitted to those extras. Margin: 8 above.
 */
export const COMPETING_SEARCH_MIN_IMPRESSIONS = 10;

/**
 * Search Console only: the smaller page's share of the two pages' impressions on a search. Below it
 * Google plainly prefers one page and the other is a sliver. ManyRequests: the thinnest labelled
 * pair's search splits 0.085 (lost at 0.10); labelled "no" pairs found: 3 at 0 to 0.03 (one at
 * 0.037), 2 at 0.05, 1 at 0.08 (one at 0.052). Margin: 0.035 above, 0.013 below. Not used on
 * estimates: they carry no impressions.
 */
export const COMPETING_SEARCH_MIN_SPLIT = 0.05;

/**
 * Share of a page's own demand (Search Console impressions, or the estimated search volume of its
 * ranked keywords) that the contested searches must cover, on at least one side, for the pair to
 * compete. Below it the overlap is a tail for both pages. The grader's cut was "among the page's
 * main searches, about 10% of its impressions". ManyRequests: the labelled pairs' larger side runs
 * from 0.131 to 0.447 (0.15 loses three); at 0.05 two more labelled "no" pairs and one not-listed
 * pair compete, at 0.12 one fewer "no" pair. SlideSpeak: both labelled pairs compete through the
 * comparison-search rule (`competes` on `CompetingPair`), not this share; the one labelled "no" pair
 * found has 0.23. Margin: 0.031 above.
 */
export const COMPETING_PAGE_MIN_SHARE = 0.1;

/**
 * Share of EACH page's own demand on the searches both pages show for (both no deeper than
 * `COMPETING_SEARCH_MAX_POSITION`, any impressions). The overlap has to matter to both pages: one
 * page's head search on which the other turns up for a handful of impressions is the other page's
 * tail, not a split (round 1's grade: most wrong calls had the overlap matter to one side only).
 * Half of `COMPETING_PAGE_MIN_SHARE`: the larger side must still reach that.
 *
 * Measured on the sprint 7 frozen data. ManyRequests: the six labelled pairs' smaller side runs from
 * 0.062 to 0.319 (at 0.07 two are lost). Of round 1's competes-calls graded wrong on live pages, 7 sit
 * at 0.044 or under and drop out at 0.05 (at 0.04 two of them stay in); one call graded good, at
 * 0.043, drops out with them. SlideSpeak: the two labelled pairs compete through the held-search rule
 * (`ownSearchHeld`), not this share. Margin: 0.012 above, 0.006 below.
 */
export const COMPETING_PAIR_MIN_BOTH_SHARE = 0.05;

/**
 * Page types that do a task on the page, and those that write about it (`pageTypeFamilyWithoutSerp`).
 * A comparison roundup ("4 ways to …, compared") reads as `versus` and writes about tools too. A
 * template page and a page of unknown type are neither: no claim is made about them. SlideSpeak: 3
 * tool-and-article pairs set aside that would otherwise compete, plus one roundup; ManyRequests: none
 * (no page reads as a tool).
 */
const TOOL_PAGE_TYPES = new Set(['online_tool']);
const ARTICLE_PAGE_TYPES = new Set(['how_to', 'listicle', 'definition', 'versus']);

/**
 * Longest search, in words, that counts. Search Console also reports machine-made and chat-style
 * questions (a templated software-evaluation family of 13 words, 12-word questions ending in "?");
 * the labelled set's grader counts them as noise. On ManyRequests' 29,900 page and query rows the
 * impressions per word count fall from 3 words down to 9 and jump at 10 (the first templated
 * families), and every cut from 6 to 10 gives the same pairs; 10 keeps the most real searches. Over
 * it, two noise-only extras drop out (12 and 13 words). SlideSpeak's keywords change nothing.
 */
export const COMPETING_SEARCH_MAX_WORDS = 10;

/**
 * Words that never change what a search asks for. Deliberately small: anything that names a thing
 * or a task stays, so an abbreviation and the word it stands for, or two near-synonyms, stay
 * different searches.
 */
const FUNCTION_WORDS = new Set([
  'a', 'an', 'the', 'of', 'for', 'in', 'on', 'with', 'by', 'at', 'and', 'or',
  'my', 'your', 'i', 'do', 'does', 'can', 'is', 'are', 'what', 'how'
]);

/**
 * In a search with a direction ("jpg to png"), these only restate the "to": "convert from jpg to
 * png", "jpg to png converter" and "jpg to png" ask for the same file. They are kept in a search
 * without a direction, where they may be the subject ("png converter").
 */
const DIRECTION_VERBS = new Set([
  'convert', 'converts', 'converter', 'converters', 'conversion', 'converting', 'from', 'change', 'changing'
]);

/** "slides" → "slide", "templates" → "template", "agencies" → "agency"; short words stay. */
function singular(word: string): string {
  if (word.length > 4 && word.endsWith('ies')) return `${word.slice(0, -3)}y`;
  if (word.length > 3 && word.endsWith('s') && !/(ss|us|is)$/.test(word)) return word.slice(0, -1);
  return word;
}

/**
 * The words of a text as `competingSearchKey` compares them (singular, function words and the
 * "to" left out), for a containment test: every word of a search in a page's title.
 */
export function competingSearchWords(text: string): Set<string> {
  return new Set(
    normalizeCompetingSearch(text)
      .split(' ')
      .filter((w) => w && w !== 'to' && w !== 'into' && !FUNCTION_WORDS.has(w))
      .map(singular)
  );
}

/** Lower case, letters and digits only, single spaces. */
export function normalizeCompetingSearch(search: string): string {
  return String(search || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

/**
 * One search, however it is typed: the same words in any order, singular or plural, with or without
 * function words. A search with a direction ("X to Y") keeps it, so "jpg to png" and "png to jpg" are
 * two searches (opposite conversions are different jobs). A search with two directions is matched
 * only word for word. Never returns a key for an empty search.
 *
 * This is not word overlap: two searches meet only when, after this normalising, they are the same.
 * "invoice template" and "receipt template" share a word and stay apart.
 */
export function competingSearchKey(search: string): string | null {
  const words = normalizeCompetingSearch(search).replace(/\bhow to\b/g, 'how').split(' ').filter(Boolean);
  if (!words.length) return null;
  const parts: string[][] = [[]];
  for (const w of words) {
    if (w === 'to' || w === 'into') parts.push([]);
    else parts[parts.length - 1].push(w);
  }
  if (parts.length > 2) return `=${words.join(' ')}`;
  const directional = parts.length === 2;
  const keyed = parts.map((p) =>
    [...new Set(p.filter((w) => !FUNCTION_WORDS.has(w) && !(directional && DIRECTION_VERBS.has(w))).map(singular))].sort()
  );
  if (keyed.some((p) => p.length === 0)) return `=${words.join(' ')}`;
  return directional ? `${keyed[0].join(' ')} > ${keyed[1].join(' ')}` : keyed[0].join(' ');
}

/** One page seen on one search. */
export interface CompetingObservation {
  pageId: string;
  /** The search as worded by its source. */
  search: string;
  kind: CompetingObservationKind;
  /** Average position (Search Console, impression-weighted) or stored position; null = not in the results. */
  position: number | null;
  clicks?: number | null;
  impressions?: number | null;
  /** Estimated monthly searches (ranked keywords). */
  searchVolume?: number | null;
  /** The site's own name is in the search: says nothing about which page serves it. */
  branded?: boolean;
  /**
   * The search asks for the opposite of the page's task (`searchRunsAgainstPageTask`) or the reverse
   * of its conversion (`searchRunsAgainstPageDirection`).
   */
  againstPageTask?: boolean;
}

export type CompetingPageExclusion =
  /** A comparison, alternatives or "vs" page: a different job, never folded into another page. */
  | 'comparison_page'
  /** A taxonomy archive or a paginated listing: overlaps every post it lists by construction. */
  | 'archive'
  | 'homepage'
  /** The page's job is not search (`page-role.ts`: a menu or footer page, a showcase section). */
  | 'not_a_search_page'
  /** The latest crawl found it redirected to another page, or gone: Search Console rows from before are history. */
  | 'not_live';

export type CompetingPairExclusion = CompetingPageExclusion | 'hub_and_facet';

export interface CompetingPageInput {
  pageId: string;
  url: string;
  exclusion: CompetingPageExclusion | null;
  /** The page's title (site suffix stripped) and H1, for the duplicate test. */
  title?: string | null;
  h1?: string | null;
  /** The page's canonical is itself (or it declares none): it means to be indexed on its own. */
  selfCanonical?: boolean;
  /** The page's own type (`pageTypeFamilyWithoutSerp`): an online tool, a how-to, a listicle… */
  pageType?: string | null;
}

export interface CompetingPagesInput {
  source: CompetingEvidenceSource;
  pages: CompetingPageInput[];
  observations: CompetingObservation[];
  /** One page's path is an ancestor of the other's (hub and facet): the overlap is the hierarchy. */
  isHubAndFacet?: (urlA: string, urlB: string) => boolean;
  /** Only pairs with at least one of these pages (default: every pair). */
  forPageIds?: ReadonlySet<string>;
  /**
   * The held-search rule: deepest position at which the other page holds a page's own comparison
   * search. The loader passes the held-back card's page-one line (`SERP_HELD_BACK_OWN_SEARCH_MAX_POSITION`);
   * without it the rule is off.
   */
  ownSearchHolderMaxPosition?: number;
}

/** One page's numbers on one shared search. */
export interface CompetingSide {
  /** Search Console clicks; null on estimates. */
  clicks: number | null;
  /** Search Console impressions; null on estimates. */
  impressions: number | null;
  /** Best position the page is seen at for the search; null when it is in no result. */
  position: number | null;
  /** Estimated monthly searches; null on Search Console. */
  searchVolume: number | null;
  /** The wordings this page was seen on (estimates can see one search worded several ways). */
  searches: string[];
  kinds: CompetingObservationKind[];
  /** The search is this page's own comparison search. */
  comparisonSearch: boolean;
  /**
   * Best position from an observation with search demand behind it (a Search Console row, or a
   * ranked keyword with volume); null when the page is seen only in a stored result set.
   */
  rankedPosition: number | null;
  /**
   * The one wording this page holds, with its own position and volume, from the same observation
   * (the one with the most demand; then the best position). On estimates a search can be held as
   * several wordings with different volumes, so `position` and `searchVolume` above can come from two
   * of them: a card that quotes one wording quotes this. On Search Console the query itself.
   */
  wording: { search: string; position: number | null; searchVolume: number | null };
}

/** A search both pages are seen on, with each side's numbers. Readable by any card. */
export interface CompetingSharedSearch {
  /** The most visible wording of the search. */
  search: string;
  /** `competingSearchKey` (estimates) or the normalised query (Search Console). */
  key: string;
  a: CompetingSide;
  b: CompetingSide;
  /** Both pages really show for it (`competingSearchContested`), and no third page holds it. */
  contested: boolean;
  /**
   * Another page of the site holds this search clearly better than both (more impressions or volume
   * than the two together, and a better position than either): the search is that page's, so it
   * does not make these two compete.
   */
  heldBy: string | null;
}

/** One search a page earns on its own, with its numbers. */
export interface CompetingOwnSearch {
  search: string;
  /** Search Console average position, or best stored position. */
  position: number | null;
  /** Search Console only; null on estimates. */
  clicks: number | null;
  impressions: number | null;
  /** Estimated monthly searches; null on Search Console. */
  searchVolume: number | null;
}

/**
 * One page's searches of its own against the other page of its pair (quality sprint 7, workstream B):
 * searches the page **earns**, which the other page neither shows for nor is for (its comparison
 * search). Earned means real demand at a real position, from the thresholds the pair test already
 * uses: no deeper than `COMPETING_SEARCH_MAX_POSITION`, and with Search Console at least
 * `COMPETING_SEARCH_MIN_IMPRESSIONS` impressions; on estimates, a ranked keyword with search volume
 * (a stored result set alone shows no demand). The verdict reads it: a page with no search of its own "adds nothing" and can be merged.
 */
export interface CompetingOwnSearches {
  /** Searches the page earns that the other page does not show for. */
  count: number;
  /** Their demand: impressions (Search Console) or estimated monthly searches. */
  demand: number;
  /** Their clicks; null on estimates. */
  clicks: number | null;
  /**
   * Of those, the ones no third live page of the site shows for either (no deeper than
   * `COMPETING_SEARCH_MAX_POSITION`): what the site would lose with this page. A search the page
   * shares with a third page is still served when the page is merged away.
   */
  uncoveredCount: number;
  uncoveredDemand: number;
  /** The page's whole demand and clicks, noise excluded (clicks null on estimates). */
  totalDemand: number;
  totalClicks: number | null;
  /** The biggest of the page's own searches, most demand first (at most `COMPETING_OWN_SEARCHES_LISTED`). */
  top: CompetingOwnSearch[];
}

/** How many of a page's own searches a pair carries by name, for a card to quote. */
export const COMPETING_OWN_SEARCHES_LISTED = 3;

export interface CompetingPair {
  a: { pageId: string; url: string };
  b: { pageId: string; url: string };
  source: CompetingEvidenceSource;
  /** Contested searches first, then by combined weight (impressions or search volume). */
  sharedSearches: CompetingSharedSearch[];
  /**
   * The pair competes when any of these holds (false = keep both):
   * - the overlap matters to both pages: a contested search, contested searches covering at least
   *   `COMPETING_PAGE_MIN_SHARE` of one page's demand, and the searches both show for covering at
   *   least `COMPETING_PAIR_MIN_BOTH_SHARE` of each page's;
   * - `ownSearchHeld` on either side;
   * - `duplicate`.
   * ManyRequests: the held-search and duplicate halves add no labelled pair the shares do not find;
   * SlideSpeak: both labelled pairs rest on the held search.
   */
  competes: boolean;
  /** Share of each page's own demand on the contested searches, 0–1 (impressions or search volume). */
  contestedShare: { a: number; b: number };
  /** A contested search is that page's own comparison search. */
  ownSearchContested: { a: boolean; b: boolean };
  /**
   * That page's own comparison search is contested and the OTHER page ranks on page one for it
   * (`ownSearchHolderMaxPosition`): Google already answers this page's search with the other page.
   */
  ownSearchHeld: { a: boolean; b: boolean };
  /**
   * Share of each page's own demand on the searches both pages show for (no deeper than
   * `COMPETING_SEARCH_MAX_POSITION`), 0–1.
   */
  overlapShare: { a: number; b: number };
  /**
   * Same title or same H1, both pages canonical to themselves, and both seen at a position on a
   * shared search: two copies of one page splitting it. Any position counts (the merge case: low
   * positions are what a split costs).
   */
  duplicate: boolean;
  /**
   * One page is a tool and the other an article (how-to, roundup, definition) on the same task:
   * different jobs, keep both (the labelled set's method and both graders). Copies aside.
   */
  differentJobs: boolean;

  /**
   * Page a's share of the pair's clicks and impressions on the contested searches, 0–1 (page b has
   * the rest). Search Console only: estimates carry no measured split, and say so with null.
   */
  split: { clicks: number | null; impressions: number | null } | null;
  /**
   * The pair competes on its overlap (the first half of `competes`): not only through a held search
   * or as copies. What the verdict reads before it advises a merge.
   */
  competesOnOverlap: boolean;
  /**
   * Each page's searches of its own (`CompetingOwnSearches`), on competing pairs only (null on a
   * pair that does not compete: nothing reads it there).
   */
  ownSearches: { a: CompetingOwnSearches; b: CompetingOwnSearches } | null;
  /** Both pages are compared on the same search. */
  sameComparisonSearch: boolean;
  /** One page appears in a stored result set fetched for the other (estimates only). */
  inOneResultSet: boolean;
  /** The old engine's exclusions; an excluded pair is reported, never turned into advice. */
  exclusion: CompetingPairExclusion | null;
}

export interface CompetingPagesResult {
  source: CompetingEvidenceSource;
  /** Pairs with no exclusion, competing first. */
  pairs: CompetingPair[];
  /** Pairs an exclusion rule removed, with the reason. */
  excluded: CompetingPair[];
  /** Title (site suffix stripped) and H1 of every page in a pair, for a card that names a change. */
  pageTexts: Record<string, { title: string | null; h1: string | null }>;
}

/** An observation the pair test ignores: branded, too long to be a person's search, or off-task. */
export function competingObservationIsNoise(o: CompetingObservation): boolean {
  if (o.branded || o.againstPageTask) return true;
  return normalizeCompetingSearch(o.search).split(' ').length > COMPETING_SEARCH_MAX_WORDS;
}

/** The key two observations must share: word for word on Search Console, `competingSearchKey` on estimates. */
export function competingObservationKey(source: CompetingEvidenceSource, search: string): string | null {
  if (source === 'search_console') return normalizeCompetingSearch(search) || null;
  return competingSearchKey(search);
}

/** Demand weight of one observation: impressions (Search Console) or estimated volume (ranked keyword). */
function observationWeight(o: CompetingObservation): number {
  if (o.kind === 'search_console') return Math.max(0, Number(o.impressions) || 0);
  if (o.kind === 'ranked_keyword') return Math.max(0, Number(o.searchVolume) || 0);
  return 0;
}

function sideOf(obs: CompetingObservation[]): CompetingSide {
  const positions = obs.map((o) => o.position).filter((p): p is number => typeof p === 'number' && p > 0);
  const sum = (f: (o: CompetingObservation) => number | null | undefined): number =>
    obs.reduce((t, o) => t + (Number(f(o)) || 0), 0);
  const isSc = obs.some((o) => o.kind === 'search_console');
  const volumes = obs.map((o) => Number(o.searchVolume) || 0);
  return {
    clicks: isSc ? sum((o) => o.clicks) : null,
    impressions: isSc ? sum((o) => o.impressions) : null,
    position: positions.length ? Math.min(...positions) : null,
    searchVolume: isSc ? null : volumes.some((v) => v > 0) ? Math.max(...volumes) : null,
    searches: [...new Set(obs.map((o) => o.search))],
    kinds: [...new Set(obs.map((o) => o.kind))],
    comparisonSearch: obs.some((o) => o.kind === 'comparison_search'),
    rankedPosition: (() => {
      const ranked = obs
        .filter((o) => (o.kind === 'search_console' && (Number(o.impressions) || 0) > 0) || (o.kind === 'ranked_keyword' && (Number(o.searchVolume) || 0) > 0))
        .map((o) => o.position)
        .filter((p): p is number => typeof p === 'number' && p > 0);
      return ranked.length ? Math.min(...ranked) : null;
    })(),
    wording: (() => {
      const best = [...obs].sort(
        (x, y) =>
          observationWeight(y) - observationWeight(x) ||
          (x.position ?? Infinity) - (y.position ?? Infinity) ||
          (x.search < y.search ? -1 : 1)
      )[0];
      const position = typeof best.position === 'number' && best.position > 0 ? best.position : null;
      return { search: best.search, position, searchVolume: isSc ? null : Number(best.searchVolume) || null };
    })()
  };
}

const showsWithin = (side: CompetingSide): boolean =>
  side.position !== null && side.position <= COMPETING_SEARCH_MAX_POSITION;

/**
 * Both pages really show for the search.
 *
 * - Search Console: each page has at least `COMPETING_SEARCH_MIN_IMPRESSIONS`, an average position no
 *   deeper than `COMPETING_SEARCH_MAX_POSITION`, and the smaller page holds at least
 *   `COMPETING_SEARCH_MIN_SPLIT` of the two pages' impressions.
 * - Estimated: each page ranks no deeper than `COMPETING_SEARCH_MAX_POSITION`, or the search is that
 *   page's own comparison search (it is what the page is for, ranking or not); and at least one of
 *   them ranks. Two pages compared on one search neither ranks for are a head term neither earns.
 */
export function competingSearchContested(
  source: CompetingEvidenceSource,
  a: CompetingSide,
  b: CompetingSide
): boolean {
  if (source === 'search_console') {
    const ia = a.impressions ?? 0;
    const ib = b.impressions ?? 0;
    if (ia < COMPETING_SEARCH_MIN_IMPRESSIONS || ib < COMPETING_SEARCH_MIN_IMPRESSIONS) return false;
    if (!showsWithin(a) || !showsWithin(b)) return false;
    return Math.min(ia, ib) / (ia + ib) >= COMPETING_SEARCH_MIN_SPLIT;
  }
  const okA = showsWithin(a) || a.comparisonSearch;
  const okB = showsWithin(b) || b.comparisonSearch;
  return okA && okB && (showsWithin(a) || showsWithin(b));
}

/**
 * Search Console positions are averages over many days, so the side's position on a query is the
 * impression-weighted average of its rows; estimates keep the best stored position.
 */
function searchConsoleSide(obs: CompetingObservation[]): CompetingSide {
  const side = sideOf(obs);
  const imps = obs.reduce((t, o) => t + (Number(o.impressions) || 0), 0);
  const weighted = obs.reduce((t, o) => t + (Number(o.impressions) || 0) * (Number(o.position) || 0), 0);
  const position = imps > 0 ? weighted / imps : side.position;
  return { ...side, position, wording: { ...side.wording, position } };
}

const pairExclusion = (
  a: CompetingPageInput,
  b: CompetingPageInput,
  isHubAndFacet?: (urlA: string, urlB: string) => boolean
): CompetingPairExclusion | null =>
  a.exclusion ?? b.exclusion ?? (isHubAndFacet && isHubAndFacet(a.url, b.url) ? 'hub_and_facet' : null);

const sameText = (x: string | null | undefined, y: string | null | undefined): boolean => {
  const a = normalizeCompetingSearch(x || '');
  return a.length > 0 && a === normalizeCompetingSearch(y || '');
};

/** Same title or same H1, and both pages canonical to themselves. */
export function pagesAreDuplicates(a: CompetingPageInput, b: CompetingPageInput): boolean {
  if (!a.selfCanonical || !b.selfCanonical) return false;
  return sameText(a.title, b.title) || sameText(a.h1, b.h1);
}

/** The two sides of a text's "X to Y", keyed like `competingSearchKey`; null without exactly one. */
function directionSides(text: string): [Set<string>, Set<string>] | null {
  const key = competingSearchKey(text);
  if (!key || key.startsWith('=') || !key.includes(' > ')) return null;
  const [from, to] = key.split(' > ');
  return [new Set(from.split(' ')), new Set(to.split(' '))];
}

/**
 * The search asks for the reverse of the conversion the page names ("Y to X" against a page that
 * turns X into Y). The task test (`searchRunsAgainstPageTask`) reads actions that undo each other;
 * this reads the direction, from the same words: the page's title or H1 says "X to Y", the search
 * puts the page's target words before its "to" and the page's source words after, and shares
 * neither side the page's way round. A page or search without a direction is never caught.
 */
export function searchRunsAgainstPageDirection(
  pageTexts: ReadonlyArray<string | null | undefined>,
  search: string
): boolean {
  const s = directionSides(search);
  if (!s) return false;
  const meets = (x: Set<string>, y: Set<string>): boolean => [...x].some((w) => y.has(w));
  return pageTexts.some((t) => {
    const p = t ? directionSides(t) : null;
    if (!p) return false;
    return meets(s[0], p[1]) && meets(s[1], p[0]) && !meets(s[0], p[0]) && !meets(s[1], p[1]);
  });
}

/** Every pair of the site's pages that share a search, with the evidence; pure, deterministic. */
export function buildCompetingPairs(input: CompetingPagesInput): CompetingPagesResult {
  const pageById = new Map(input.pages.map((p) => [p.pageId, p]));
  // page → key → observations; page → total demand weight (noise excluded).
  const byPage = new Map<string, Map<string, CompetingObservation[]>>();
  const totalWeight = new Map<string, number>();
  const pagesByKey = new Map<string, Set<string>>();
  for (const o of input.observations) {
    if (!pageById.has(o.pageId) || competingObservationIsNoise(o)) continue;
    const key = competingObservationKey(input.source, o.search);
    if (!key) continue;
    let keys = byPage.get(o.pageId);
    if (!keys) byPage.set(o.pageId, (keys = new Map()));
    const list = keys.get(key) ?? [];
    list.push(o);
    keys.set(key, list);
    totalWeight.set(o.pageId, (totalWeight.get(o.pageId) ?? 0) + observationWeight(o));
    const pages = pagesByKey.get(key) ?? new Set<string>();
    pages.add(o.pageId);
    pagesByKey.set(key, pages);
  }

  const comparisonKeys = new Map<string, Set<string>>();
  for (const [pageId, keys] of byPage) {
    for (const [key, obs] of keys) {
      if (obs.some((o) => o.kind === 'comparison_search')) {
        const s = comparisonKeys.get(pageId) ?? new Set<string>();
        s.add(key);
        comparisonKeys.set(pageId, s);
      }
    }
  }

  const sideFor = (pageId: string, key: string): CompetingSide => {
    const obs = byPage.get(pageId)!.get(key)!;
    return input.source === 'search_console' ? searchConsoleSide(obs) : sideOf(obs);
  };
  const weightOn = (pageId: string, key: string): number =>
    byPage.get(pageId)!.get(key)!.reduce((t, o) => t + observationWeight(o), 0);
  const thirdPageHolding = (key: string, idA: string, idB: string, a: CompetingSide, b: CompetingSide): string | null => {
    if (a.position === null || b.position === null) return null;
    const pairWeight = weightOn(idA, key) + weightOn(idB, key);
    let holder: string | null = null;
    for (const c of pagesByKey.get(key) ?? []) {
      if (c === idA || c === idB) continue;
      const side = sideFor(c, key);
      if (side.position === null) continue;
      if (weightOn(c, key) > pairWeight && side.position < Math.min(a.position, b.position)) {
        if (!holder || c < holder) holder = c;
      }
    }
    return holder;
  };

  const isSc = input.source === 'search_console';
  const earns = (id: string, key: string): boolean => {
    const side = sideFor(id, key);
    if (!showsWithin(side)) return false;
    const w = weightOn(id, key);
    return isSc ? w >= COMPETING_SEARCH_MIN_IMPRESSIONS : side.rankedPosition !== null && w > 0;
  };
  const ownSearchesOf = (id: string, otherId: string): CompetingOwnSearches => {
    const theirs = byPage.get(otherId);
    const own: Array<{ key: string; weight: number; clicks: number }> = [];
    let totalClicks = 0;
    let uncoveredCount = 0;
    let uncoveredDemand = 0;
    for (const [key, obs] of byPage.get(id)!) {
      const clicks = obs.reduce((t, o) => t + (Number(o.clicks) || 0), 0);
      totalClicks += clicks;
      if (!earns(id, key)) continue;
      // The other page shows for it, or it is the search the other page is for: shared, not own.
      if (theirs?.has(key) && (showsWithin(sideFor(otherId, key)) || sideFor(otherId, key).comparisonSearch)) continue;
      const weight = weightOn(id, key);
      own.push({ key, weight, clicks });
      const covered = [...(pagesByKey.get(key) ?? [])].some(
        (c) => c !== id && c !== otherId && pageById.get(c)?.exclusion !== 'not_live' && showsWithin(sideFor(c, key))
      );
      if (!covered) {
        uncoveredCount += 1;
        uncoveredDemand += weight;
      }
    }
    own.sort((x, y) => y.weight - x.weight || (x.key < y.key ? -1 : 1));
    return {
      count: own.length,
      demand: own.reduce((t, o) => t + o.weight, 0),
      clicks: isSc ? own.reduce((t, o) => t + o.clicks, 0) : null,
      uncoveredCount,
      uncoveredDemand,
      totalDemand: totalWeight.get(id) ?? 0,
      totalClicks: isSc ? totalClicks : null,
      top: own.slice(0, COMPETING_OWN_SEARCHES_LISTED).map(({ key }) => {
        const side = sideFor(id, key);
        const wordings = [...byPage.get(id)!.get(key)!].sort(
          (x, y) => observationWeight(y) - observationWeight(x) || (x.search < y.search ? -1 : 1)
        );
        return {
          search: wordings[0].search,
          position: side.position,
          clicks: side.clicks,
          impressions: side.impressions,
          searchVolume: side.searchVolume
        };
      })
    };
  };

  const pairKeys = new Map<string, string[]>();
  for (const [key, pages] of pagesByKey) {
    if (pages.size < 2) continue;
    const ids = [...pages].sort();
    for (let i = 0; i < ids.length; i++) {
      for (let j = i + 1; j < ids.length; j++) {
        if (input.forPageIds && !input.forPageIds.has(ids[i]) && !input.forPageIds.has(ids[j])) continue;
        const k = `${ids[i]}|${ids[j]}`;
        const list = pairKeys.get(k) ?? [];
        list.push(key);
        pairKeys.set(k, list);
      }
    }
  }

  const pairs: CompetingPair[] = [];
  const excluded: CompetingPair[] = [];
  for (const [k, keys] of pairKeys) {
    const [idA, idB] = k.split('|');
    const pa = pageById.get(idA)!;
    const pb = pageById.get(idB)!;
    const shared: CompetingSharedSearch[] = keys.map((key) => {
      const oa = byPage.get(idA)!.get(key)!;
      const ob = byPage.get(idB)!.get(key)!;
      const a = input.source === 'search_console' ? searchConsoleSide(oa) : sideOf(oa);
      const b = input.source === 'search_console' ? searchConsoleSide(ob) : sideOf(ob);
      const wordings = [...oa, ...ob].sort((x, y) => observationWeight(y) - observationWeight(x) || (x.search < y.search ? -1 : 1));
      const heldBy = thirdPageHolding(key, idA, idB, a, b);
      return {
        search: wordings[0].search,
        key,
        a,
        b,
        contested: !heldBy && competingSearchContested(input.source, a, b),
        heldBy
      };
    });
    const weightOf = (s: CompetingSharedSearch): number =>
      (s.a.impressions ?? s.a.searchVolume ?? 0) + (s.b.impressions ?? s.b.searchVolume ?? 0);
    shared.sort(
      (x, y) => Number(y.contested) - Number(x.contested) || weightOf(y) - weightOf(x) || (x.key < y.key ? -1 : 1)
    );
    const contested = shared.filter((s) => s.contested);
    const contestedWeight = (id: string): number =>
      contested.reduce((t, s) => t + byPage.get(id)!.get(s.key)!.reduce((u, o) => u + observationWeight(o), 0), 0);
    const share = (id: string): number => {
      const total = totalWeight.get(id) ?? 0;
      return total > 0 ? contestedWeight(id) / total : 0;
    };
    const contestedShare = { a: share(idA), b: share(idB) };
    const bothShow = shared.filter((x) => !x.heldBy && showsWithin(x.a) && showsWithin(x.b));
    const overlap = (id: string): number => {
      const total = totalWeight.get(id) ?? 0;
      if (total <= 0) return 0;
      const w = bothShow.reduce((t, x) => t + byPage.get(id)!.get(x.key)!.reduce((u, o) => u + observationWeight(o), 0), 0);
      return w / total;
    };
    const overlapShare = { a: overlap(idA), b: overlap(idB) };
    const holderMax = input.ownSearchHolderMaxPosition;
    const holds = (holder: CompetingSide): boolean =>
      holderMax !== undefined && holder.rankedPosition !== null && holder.rankedPosition <= holderMax;
    const ownSearchHeld = {
      a: contested.some((x) => x.a.comparisonSearch && holds(x.b)),
      b: contested.some((x) => x.b.comparisonSearch && holds(x.a))
    };
    // Copies split a search when Google shows both, however low: at least one shared search where
    // both are seen at a position. Two copies compared on one search neither appears for (translated
    // legal pages with one title in two languages) are not splitting anything.
    const duplicate =
      pagesAreDuplicates(pa, pb) && shared.some((x) => x.a.position !== null && x.b.position !== null);
    const differentJobs =
      (TOOL_PAGE_TYPES.has(pa.pageType ?? '') && ARTICLE_PAGE_TYPES.has(pb.pageType ?? '')) ||
      (TOOL_PAGE_TYPES.has(pb.pageType ?? '') && ARTICLE_PAGE_TYPES.has(pa.pageType ?? ''));

    const ownSearchContested = {
      a: contested.some((s) => s.a.comparisonSearch),
      b: contested.some((s) => s.b.comparisonSearch)
    };
    let split: CompetingPair['split'] = null;
    if (input.source === 'search_console') {
      const sum = (f: (s: CompetingSharedSearch) => number) => contested.reduce((t, s) => t + f(s), 0);
      const ca = sum((s) => s.a.clicks ?? 0);
      const cb = sum((s) => s.b.clicks ?? 0);
      const ia = sum((s) => s.a.impressions ?? 0);
      const ib = sum((s) => s.b.impressions ?? 0);
      split = { clicks: ca + cb > 0 ? ca / (ca + cb) : null, impressions: ia + ib > 0 ? ia / (ia + ib) : null };
    }
    const competesOnOverlap =
      !differentJobs &&
      contested.length > 0 &&
      Math.max(contestedShare.a, contestedShare.b) >= COMPETING_PAGE_MIN_SHARE &&
      Math.min(overlapShare.a, overlapShare.b) >= COMPETING_PAIR_MIN_BOTH_SHARE;
    const competes = duplicate || competesOnOverlap || (!differentJobs && (ownSearchHeld.a || ownSearchHeld.b));
    const ckA = comparisonKeys.get(idA);
    const ckB = comparisonKeys.get(idB);
    const pair: CompetingPair = {
      a: { pageId: idA, url: pa.url },
      b: { pageId: idB, url: pb.url },
      source: input.source,
      sharedSearches: shared,
      competes,
      contestedShare,
      ownSearchContested,
      ownSearchHeld,
      overlapShare,
      duplicate,
      differentJobs,
      split,
      competesOnOverlap,
      ownSearches: competes ? { a: ownSearchesOf(idA, idB), b: ownSearchesOf(idB, idA) } : null,
      sameComparisonSearch: Boolean(ckA && ckB && [...ckA].some((key) => ckB.has(key))),
      inOneResultSet: shared.some(
        (s) =>
          (s.a.comparisonSearch && s.b.kinds.includes('result_set')) ||
          (s.b.comparisonSearch && s.a.kinds.includes('result_set'))
      ),
      exclusion: pairExclusion(pa, pb, input.isHubAndFacet)
    };
    (pair.exclusion ? excluded : pairs).push(pair);
  }
  const order = (x: CompetingPair, y: CompetingPair): number =>
    Number(y.competes) - Number(x.competes) ||
    Math.max(y.contestedShare.a, y.contestedShare.b) - Math.max(x.contestedShare.a, x.contestedShare.b) ||
    (x.a.pageId + x.b.pageId < y.a.pageId + y.b.pageId ? -1 : 1);
  pairs.sort(order);
  excluded.sort(order);
  const pageTexts: CompetingPagesResult['pageTexts'] = {};
  for (const p of [...pairs, ...excluded]) {
    for (const id of [p.a.pageId, p.b.pageId]) {
      const page = pageById.get(id);
      if (page && !pageTexts[id]) pageTexts[id] = { title: page.title ?? null, h1: page.h1 ?? null };
    }
  }
  return { source: input.source, pairs, excluded, pageTexts };
}

/** The pairs one page is in, the page always as side `a` (sides swapped where needed). */
export function competingPairsForPage(result: CompetingPagesResult, pageId: string): CompetingPair[] {
  return result.pairs
    .filter((p) => p.a.pageId === pageId || p.b.pageId === pageId)
    .map((p) => (p.a.pageId === pageId ? p : swapCompetingPair(p)));
}

/** The same pair seen from page b. */
export function swapCompetingPair(p: CompetingPair): CompetingPair {
  return {
    ...p,
    a: p.b,
    b: p.a,
    sharedSearches: p.sharedSearches.map((s) => ({ ...s, a: s.b, b: s.a })),
    contestedShare: { a: p.contestedShare.b, b: p.contestedShare.a },
    ownSearchContested: { a: p.ownSearchContested.b, b: p.ownSearchContested.a },
    ownSearchHeld: { a: p.ownSearchHeld.b, b: p.ownSearchHeld.a },
    overlapShare: { a: p.overlapShare.b, b: p.overlapShare.a },
    ownSearches: p.ownSearches ? { a: p.ownSearches.b, b: p.ownSearches.a } : null,
    split: p.split
      ? {
          clicks: p.split.clicks === null ? null : 1 - p.split.clicks,
          impressions: p.split.impressions === null ? null : 1 - p.split.impressions
        }
      : null
  };
}
